import { useContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";
import type { ContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  CARD_CONTENT_OVERLAY_TABLES,
  type CardReviewSettings,
  cardReviewSettingsFromUser,
  isCardContentAvailable,
  isCardContentKindReviewEligible,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  type DueUserCard,
  useDueUserCardsQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  isCardContentKindInReviewFocus,
  type ReviewFocus,
} from "@-/dev.improve.haniverse.lib.learn.review-focus";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import type { QueryClient } from "@-/com.npmjs.tanstack__react-query";

const DUE_ROWS_COUNT_TABLES = [
  "user",
  "user_card",
  "fsrs_card",
  ...CARD_CONTENT_OVERLAY_TABLES,
] as const;

/**
 * The query key for a count over the due cards, under `name`. Registered against every table the
 * count reads so passive consumers re-fetch after a matching committed write.
 */
export function useGetDueCardCountQueryKey<const Name extends string>(name: Name) {
  const userSettingsQueryOptions = actorQueries(
    import.meta.location,
  ).user.findFirst.useQueryOptions({});
  const dueUserCardsQueryOptions = useDueUserCardsQueryOptions();
  const [reviewFocus] = useDeviceSetting("reviewFocus");
  const contentAvailability = useContentAvailability();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(DUE_ROWS_COUNT_TABLES));

  return () =>
    withTableChangeVersion([
      name,
      { contentAvailability, dueUserCardsQueryOptions, reviewFocus, userSettingsQueryOptions },
    ] as const);
}

type DueCardCountQueryKeyValue = ReturnType<ReturnType<typeof useGetDueCardCountQueryKey<string>>>;

/**
 * The due cards a count includes: those the next-due scheduler may serve under the user's settings
 * and review focus.
 *
 * A card whose overlay hasn't synced yet resolves to no kind. It is counted — the badge stays
 * optimistic rather than under-reporting work the scheduler may still serve — unless a review focus
 * is picked, since an unknown kind can't be known to match it.
 */
export async function readCountedDueUserCards({
  client,
  queryKey: [
    _,
    { contentAvailability, dueUserCardsQueryOptions, reviewFocus, userSettingsQueryOptions },
  ],
}: {
  client: QueryClient;
  queryKey: DueCardCountQueryKeyValue;
}): Promise<DueUserCard[]> {
  const [settingsRow, dueUserCards] = await Promise.all([
    client.fetchQuery(userSettingsQueryOptions),
    client.fetchQuery(dueUserCardsQueryOptions),
  ]);
  const cardReviewSettings = cardReviewSettingsFromUser(settingsRow);
  return dueUserCards.filter((card) =>
    isDueUserCardCounted(card, { cardReviewSettings, contentAvailability, reviewFocus }),
  );
}

function isDueUserCardCounted(
  { cardContentKind, subject }: DueUserCard,
  {
    cardReviewSettings,
    contentAvailability,
    reviewFocus,
  }: {
    cardReviewSettings: CardReviewSettings;
    contentAvailability: ContentAvailability;
    reviewFocus: ReviewFocus;
  },
): boolean {
  return cardContentKind === null
    ? reviewFocus === "all"
    : isCardContentKindReviewEligible({ cardContentKind, settings: cardReviewSettings }) &&
        isCardContentKindInReviewFocus({ cardContentKind, reviewFocus }) &&
        (subject === null ||
          isCardContentAvailable({
            cardContentKind,
            contentAvailability,
            subjectText: subject.text,
          }));
}

export function useGetDueRowsCountQueryKey() {
  return useGetDueCardCountQueryKey("dueRowsCount");
}

/**
 * Hook returning a `queryOptions` factory for the count of study cards
 * (`user_card` joined to `fsrs_card`) whose `due` is now or in the past.
 *
 * Pronunciation (pinyin flashcard) cards are always authored but only count as
 * due when the synced "Pinyin flashcards" setting is on, and only the review focus's kind counts
 * while one is picked — keep the badge in step with what the next-due-card scheduler will actually
 * serve.
 *
 * Counts over the shared due-card scan (`useDueUserCardsQueryOptions`) — the same cache entry the
 * scheduler and the learn summary read — so the badge is one SQL-filtered read, never a load of
 * the whole deck joined to every overlay.
 */
export const [useDueRowsCountQueryOptions, dueRowsCountQueryFn] = createUseQueryOptions(
  useGetDueRowsCountQueryKey,
  async ({ client, queryKey }) => {
    const counted = await readCountedDueUserCards({ client, queryKey });
    return counted.length;
  },
);
